/* Cart guard — one strap, one fitted buckle. HEllO EDC (India store).
   Added 1 Oct 2026.

   WHY

   Three orders (#MLG10303, #MLG10344, #MLG10366) reached the warehouse with a
   second, unlinked fitted buckle: an IWC Style Tang Buckle at ₹0, in a width
   that did not even fit the strap. Every buckle our own code adds carries
   _wc_group / _wc_role; these carried nothing. They were re-added by something
   that rebuilds the cart from variant ids alone — GoKwik's KwikPass cart
   restore or an abandoned-cart link — which keeps the item but drops the
   hidden "_" properties that tie a buckle to its strap.

   RULES

   A fitted buckle (SKU TYPE-FAMILY-FINISH-WIDTH-COLOUR, e.g. TG-IWC-B-18-S;
   spares end in -SP and are never touched) may only exist as the partner of
   one strap. So, working from /cart.js:

     1. A fitted buckle with no strap partner:
        - if a strap in the cart is missing its buckle and its "Buckle" label
          (and taper, when known) names exactly this buckle, the two are
          re-linked;
        - otherwise it is removed.
     2. A strap's buckle follows the strap's quantity.

     3. A strap that needs a buckle but has none gets its default buckle
        (added 10 Oct 2026, approved by Swapneel): the same ₹0 buckle the
        product page would have fitted, silver first, fetched from
        sections/hello-buckle-defaults.liquid. Only a ₹0 buckle is ever added
        automatically — if the only fitting default costs extra, nothing is
        added and the line is reported as missing_buckle instead.

      4. Watch editions (added 11 Oct 2026): a watch edition such as "GRACE in
        Suede For Cartier Tank" sells its base strap's variant (one SKU, one
        stock count). The product page does that swap itself; if an edition's
        own variant still reaches the cart some other way it arrives with no
        buckle, so rule 3 sees it, and the defaults section names its base
        twin ("e"). The edition line is then replaced by the base strap —
        same quantity and properties, plus the visible "Edition" note — with
        its default buckle. Reported as edition_swap.

  Apart from rules 3 and 4 it never adds anything, and it never removes a
  strap except to replace an edition line with its base strap (rule 4).
   Lines still carrying the old array-style properties are left alone:
   hello-cart.js repairs those first.

   COST

   Nothing on page load beyond one /cart.js read, scheduled for idle time. After
   that it reacts only to cart writes. It sends a write only when it finds
   something wrong, which is rare.

   CHECKOUT

   If the cart has changed since the last check, the checkout tap is held for
   the one /cart.js read (and a repair if needed), then released. If the cart
   is known to be clean, the tap goes straight through.

   REPORTING

   Every correction is announced as `hello_cart_guard` on the GTM dataLayer and
   through Shopify.analytics.publish, with action remove_stray / repair_pair /
   sync_qty / add_default / missing_buckle / edition_swap and the SKU, so it can be counted
   or filtered in analytics.

   Deliberately defensive: any failure is a no-op and leaves the cart exactly as
   it was. */

(function () {
  'use strict';

  if (window.__helloCartGuard) return;
  window.__helloCartGuard = true;

  var MARK = 'X-Hello-Cart-Guard';
  var FITTED = /^(TG|CL)-[A-Z]+-[A-Z]+-\d+-[A-Z]+$/i;   /* -SP spares never match */
  var MAX_PASSES = 3;

  var busy = null;          /* the running check, as a promise */
  var clean = false;        /* last check found nothing, and no cart write since */
  var passes = 0;
  var timer = null;
  var unresolved = {};      /* line keys we could not give a ₹0 default buckle */

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function req(path, payload) {
    var h = { Accept: 'application/json' };
    h[MARK] = '1';
    var init = { headers: h, credentials: 'same-origin' };
    if (payload) {
      init.method = 'POST';
      h['Content-Type'] = 'application/json';
      init.body = JSON.stringify(payload);
    }
    return fetch(root() + path, init).then(function (r) { return r.ok ? r.json() : null; });
  }

  function report(action, item) {
    var data = {
      action: action,
      sku: (item && item.sku) || '',
      variant_id: item ? item.variant_id : null,
      product_title: (item && item.product_title) || ''
    };
    try { (window.dataLayer = window.dataLayer || []).push({ event: 'hello_cart_guard', hello_cart_guard: data }); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('hello_cart_guard', data); } catch (e) {}
  }

  /* ---------- reading the cart ---------- */

  function broken(p) {
    return !!p && Object.keys(p).some(function (k) { return /^\d+$/.test(k) && Array.isArray(p[k]); });
  }
  function isFitted(item) { return FITTED.test(item.sku || ''); }

  function opt(item, re) {
    var list = item.options_with_values || [];
    for (var i = 0; i < list.length; i++) {
      if (re.test(list[i].name || '')) return String(list[i].value || '');
    }
    return '';
  }
  function norm(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); }

  /* Does this strap's "Buckle" label name this exact buckle? */
  function labelFits(strap, buckle) {
    var p = strap.properties || {};
    var label = norm(p.Buckle);
    if (!label) return false;
    var family = norm(buckle.product_title).split(' ')[0];
    var colour = norm(opt(buckle, /colou?r/i));
    var finish = norm(opt(buckle, /finish/i));
    if (!family || label.indexOf(family) !== 0) return false;
    if (colour && (' ' + label + ' ').indexOf(' ' + colour + ' ') < 0) return false;
    if (finish && (' ' + label + ' ').indexOf(' ' + finish + ' ') < 0) return false;
    var taper = parseInt(p._wc_taper, 10);
    var width = parseInt(opt(buckle, /width|size/i), 10);
    if (taper && width && taper !== width) return false;
    return true;
  }

  function plan(cart) {
    var items = (cart && cart.items) || [];
    var work = { repairs: [], updates: {}, notes: [] };

    /* Mid-repair carts are hello-cart.js's job; look again afterwards. */
    if (items.some(function (i) { return broken(i.properties); })) return null;

    var groups = {};
    items.forEach(function (i) {
      var p = i.properties || {};
      if (!p._wc_group) return;
      var g = groups[p._wc_group] || (groups[p._wc_group] = { straps: [], buckles: [] });
      (p._wc_role === 'buckle' ? g.buckles : p._wc_role === 'strap' ? g.straps : []).push(i);
    });

    /* Straps that should have a fitted buckle and have none. */
    var lonely = items.filter(function (i) {
      var p = i.properties || {};
      if (isFitted(i) || !p.Buckle) return false;
      if (!p._wc_group) return true;
      var g = groups[p._wc_group];
      return !g || !g.buckles.length;
    });

    items.forEach(function (b) {
      if (!isFitted(b)) return;
      var p = b.properties || {};
      var g = p._wc_group && p._wc_role === 'buckle' ? groups[p._wc_group] : null;
      if (g && g.straps.length) return;                 /* properly paired */

      /* Try to give it back to the strap it belongs to. */
      for (var i = 0; i < lonely.length; i++) {
        var s = lonely[i];
        if (s.quantity === b.quantity && labelFits(s, b)) {
          lonely.splice(i, 1);
          work.repairs.push({ strap: s, buckle: b });
          return;
        }
      }
      work.updates[b.key] = 0;
      work.notes.push(['remove_stray', b]);
    });

    /* Straps still without a fitted buckle after any re-link above. Only
       product_type "Watch Straps"; whether this particular strap takes a
       buckle at all (NATO, Play Metal, MIC Tape … do not) is decided by the
       defaults section, which answers only for straps with buckle_options. */
    work.missing = items.filter(function (i) {
      var p = i.properties || {};
      if (isFitted(i) || p._wc_role === 'buckle' || p._wc_role === 'spare') return false;
      if (!/watch strap/i.test(i.product_type || '')) return false;
      if (work.repairs.some(function (r) { return r.strap.key === i.key; })) return false;
      if (unresolved[i.key]) return false;
      var g = p._wc_group && groups[p._wc_group];
      return !(g && g.buckles.length);
    });

    /* Fitted buckle follows its strap's quantity. */
    Object.keys(groups).forEach(function (k) {
      var g = groups[k];
      if (!g.straps.length) return;
      var q = g.straps.reduce(function (n, s) { return n + s.quantity; }, 0);
      g.buckles.forEach(function (b) {
        if (isFitted(b) && b.quantity !== q && work.updates[b.key] === undefined) {
          work.updates[b.key] = q;
          work.notes.push(['sync_qty', b]);
        }
      });
    });

    return work;
  }

  /* ---------- fixing it ---------- */

  function newGroup() {
    return 'wc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
  }

  function relink(pair) {
    var sp = {};
    Object.keys(pair.strap.properties || {}).forEach(function (k) { sp[k] = pair.strap.properties[k]; });
    var g = sp._wc_group && sp._wc_role === 'strap' ? sp._wc_group : newGroup();
    var chain = Promise.resolve();
    if (sp._wc_group !== g || sp._wc_role !== 'strap') {
      sp._wc_group = g;
      sp._wc_role = 'strap';
      chain = req('cart/change.js', { id: pair.strap.key, quantity: pair.strap.quantity, properties: sp });
    }
    return chain.then(function () {
      var bp = {};
      Object.keys(pair.buckle.properties || {}).forEach(function (k) { bp[k] = pair.buckle.properties[k]; });
      bp._wc_group = g;
      bp._wc_role = 'buckle';
      return req('cart/change.js', { id: pair.buckle.key, quantity: pair.buckle.quantity, properties: bp });
    }).then(function () { report('repair_pair', pair.buckle); });
  }

  function fetchDefaults() {
    return fetch(root() + 'cart?section_id=hello-buckle-defaults', {
      headers: (function () { var h = {}; h[MARK] = '1'; return h; })(),
      credentials: 'same-origin'
    })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var m = /<script[^>]*data-hello-buckle-defaults[^>]*>([\s\S]*?)<\/script>/.exec(html || '');
        if (!m) return {};
        try { return JSON.parse(m[1]); } catch (e) { return {}; }
      })
      .catch(function () { return {}; });
  }

  /* Give each buckle-less strap its default ₹0 fitted buckle, linked the same
     way the product page links them. */
  function addDefaults(straps) {
    return fetchDefaults().then(function (def) {
      var adds = [];
      var chain = Promise.resolve();
      var swaps = {};                                        /* edition line key -> 0 */
      var swapped = [];
      straps.forEach(function (s) {
        var d = def[s.key];
        if (!d) { unresolved[s.key] = true; return; }        /* strap takes no buckle */
        var free = d.b && !(d.p > 0);
        if (!free && !d.e) {                                 /* nothing free fits */
          unresolved[s.key] = true;
          report('missing_buckle', s);
          return;
        }
        var sp = {};
        Object.keys(s.properties || {}).forEach(function (k) { sp[k] = s.properties[k]; });
        var g = sp._wc_group && sp._wc_role === 'strap' ? sp._wc_group : newGroup();
        sp._wc_group = g;
        sp._wc_role = 'strap';
        if (free && d.bl) sp.Buckle = d.bl;
        if (d.t) sp._wc_taper = String(d.t);

        if (d.e) {
          /* Rule 4: replace the edition's own variant with its base strap. */
          if (d.el) sp.Edition = d.el;
          if (d.eh) sp._edition_handle = d.eh;
          if (d.ev) sp._edition_variant = String(d.ev);
          if (d.et) sp._edition_title = d.et;
          if (d.ei) sp._edition_img = d.ei;
          if (!free) { delete sp._wc_group; delete sp._wc_role; report('missing_buckle', s); }
          swaps[s.key] = 0;
          swapped.push(s);
          adds.push({ id: d.e, quantity: s.quantity, properties: sp, _strap: s, _swap: true });
          if (free) adds.push({ id: d.b, quantity: s.quantity, properties: { _wc_group: g, _wc_role: 'buckle' }, _strap: s });
          return;
        }

        chain = chain.then(function () {
          return req('cart/change.js', { id: s.key, quantity: s.quantity, properties: sp });
        });
        adds.push({ id: d.b, quantity: s.quantity, properties: { _wc_group: g, _wc_role: 'buckle' }, _strap: s });
      });
      if (!adds.length) return false;
      return chain
        .then(function () {
          return req('cart/add.js', { items: adds.map(function (a) { return { id: a.id, quantity: a.quantity, properties: a.properties }; }) });
        })
        .then(function (res) {
          if (!res) { adds.forEach(function (a) { unresolved[a._strap.key] = true; }); return false; }
          adds.forEach(function (a) { report(a._swap ? 'edition_swap' : 'add_default', a._strap); });
          /* The edition line goes only once its base strap is safely in the
             cart (adding never changes the keys of existing lines). */
          if (!swapped.length) return true;
          return req('cart/update.js', { updates: swaps }).then(function () { return true; });
        });
    });
  }

  function check() {
    if (busy) return busy;
    busy = req('cart.js')
      .then(function (cart) {
        var work = plan(cart);
        if (!work) { clean = false; return; }
        var hasUpdates = Object.keys(work.updates).length > 0;
        var hasMissing = work.missing && work.missing.length > 0;
        if (!work.repairs.length && !hasUpdates && !hasMissing) { clean = true; passes = 0; return; }
        if (passes >= MAX_PASSES) { clean = false; return; }
        passes++;

        /* Removals and quantities first (their keys are still valid), then
           the re-links, which change the keys of the lines they touch. */
        var chain = hasUpdates ? req('cart/update.js', { updates: work.updates }) : Promise.resolve();
        return chain
          .then(function () {
            work.notes.forEach(function (n) { report(n[0], n[1]); });
            return work.repairs.reduce(function (c, pair) {
              return c.then(function () { return relink(pair); });
            }, Promise.resolve());
          })
          .then(function () {
            /* Line keys change when properties change, so the missing list is
               only acted on in a pass where nothing else was repaired. */
            if (hasMissing && !work.repairs.length && !hasUpdates) return addDefaults(work.missing);
          })
          .then(function () {
            try { document.dispatchEvent(new CustomEvent('wc:cart-synced')); } catch (e) {}
            busy = null;
            return check();            /* confirm the cart is now clean */
          });
      })
      .catch(function () { clean = false; })
      .then(function () { busy = null; });
    return busy;
  }

  function schedule(delay) {
    clean = false;
    clearTimeout(timer);
    timer = setTimeout(function () { passes = 0; check(); }, delay);
  }

  /* ---------- watching for cart writes ---------- */

  function isCartWrite(url) {
    return typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url);
  }
  function oursByHeaders(init) {
    try {
      var h = init && init.headers;
      if (!h) return false;
      if (typeof h.get === 'function') return !!h.get(MARK);
      return Object.keys(h).some(function (k) { return k.toLowerCase() === MARK.toLowerCase(); });
    } catch (e) { return false; }
  }

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var out = nativeFetch.apply(this, arguments);
      if (isCartWrite(url) && !oursByHeaders(init)) {
        clean = false;
        out.then(function () { schedule(300); }, function () {});
      }
      return out;
    };
  }

  var XHR = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
  if (XHR) {
    var open = XHR.open, send = XHR.send;
    XHR.open = function (method, url) { this.__hcgUrl = url; return open.apply(this, arguments); };
    XHR.send = function () {
      var self = this;
      if (isCartWrite(self.__hcgUrl)) {
        clean = false;
        this.addEventListener('loadend', function () { schedule(300); });
      }
      return send.apply(this, arguments);
    };
  }

  /* ---------- the checkout tap ---------- */

  var CHECKOUT = '.gokwik-checkout button, #gokwik-buy-now, button[name="checkout"], input[name="checkout"], a[href$="/checkout"]';
  var releasing = false;

  document.addEventListener('click', function (e) {
    if (releasing || clean) return;
    var btn = e.target.closest && e.target.closest(CHECKOUT);
    if (!btn || btn.disabled) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    /* Never trap the shopper: release after the check or after 2.5 s,
       whichever comes first. */
    var done = false;
    function release() {
      if (done) return;
      done = true;
      releasing = true;
      try { btn.click(); } finally { releasing = false; }
    }
    setTimeout(release, 2500);
    passes = 0;
    check().then(release, release);
  }, true);

  /* ---------- first look ---------- */

  function firstLook() { schedule(0); }
  if ('requestIdleCallback' in window) {
    window.addEventListener('load', function () { window.requestIdleCallback(firstLook, { timeout: 3000 }); });
  } else {
    window.addEventListener('load', function () { setTimeout(firstLook, 1200); });
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) schedule(0); });
})();
